import {
  ApiError,
  createApiErrorFromResponse,
} from "@airange/shared/api";
import { LOCAL_AUTH_NON_REFRESHABLE_PATHS, LOCAL_AUTH_PATHS } from "@airange/shared/auth-routes";
import { LOGIN_PATH } from "@airange/shared/oidc-routes";
import type { UserSession } from "./auth-contract";
import { API_BASE_URL } from "./env";
import {
  clearSession,
  getAccessToken,
  getStoredAuthSource,
  setSession,
  setStoredAuthSource,
} from "./session";
import { toAppLocalUrlPath, toBrowserPath } from "./public-path";

function redirectToLogin() {
  if (typeof window === "undefined") {
    return;
  }
  const currentPath = toAppLocalUrlPath(
    `${window.location.pathname || "/"}${window.location.search || ""}${window.location.hash || ""}`,
  );
  if (currentPath === LOGIN_PATH) {
    return;
  }
  const redirect = currentPath;
  const target = `${LOGIN_PATH}?redirect=${encodeURIComponent(redirect)}`;
  window.location.assign(toBrowserPath(target));
}

const API_BASE_URL_WITHOUT_TRAILING_SLASH = API_BASE_URL.replace(/\/+$/, "");
const API_BASE_PATHNAME = getApiBasePathname(API_BASE_URL_WITHOUT_TRAILING_SLASH);
const OPENAPI_CLIENT_BASE_URL = API_BASE_URL_WITHOUT_TRAILING_SLASH.replace(/\/api$/, "");

function getApiBasePathname(apiBaseUrl: string) {
  if (!apiBaseUrl) {
    return "";
  }
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(apiBaseUrl)) {
    return new URL(`${apiBaseUrl}/`).pathname.replace(/\/+$/, "");
  }
  return `/${apiBaseUrl.replace(/^\/+/, "")}`.replace(/\/+$/, "");
}

async function readErrorDetail(response: Response) {
  const fallback = response.statusText;
  const text = await response.text();
  if (!text) {
    return fallback;
  }
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}

function hasRequestBody(init: RequestInit) {
  return init.body !== undefined && init.body !== null;
}

function buildRequestInit(
  init: RequestInit = {},
  accessTokenOverride?: string | null,
): RequestInit {
  const token = getStoredAuthSource() === "sso" ? null : (accessTokenOverride ?? getAccessToken());
  const headers = new Headers(init.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (
    hasRequestBody(init) &&
    !(typeof FormData !== "undefined" && init.body instanceof FormData) &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }
  return {
    ...init,
    credentials: "include",
    headers,
  };
}

function buildRequest(input: RequestInfo | URL, init: RequestInit = {}) {
  if (input instanceof Request) {
    return new Request(input, buildRequestInit(init));
  }
  return new Request(resolveApiRequestUrl(input), buildRequestInit(init));
}

function resolveApiRequestUrl(input: RequestInfo | URL) {
  if (input instanceof URL) {
    return input.toString();
  }
  if (typeof input !== "string") {
    return input.url;
  }
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(input)) {
    return input;
  }
  if (input.startsWith("/")) {
    return `${API_BASE_URL_WITHOUT_TRAILING_SLASH}${input}`;
  }
  return `${API_BASE_URL_WITHOUT_TRAILING_SLASH}/${input}`;
}

function extractApiRequestPath(input: RequestInfo | URL) {
  const resolvedUrl = resolveApiRequestUrl(input);
  if (!/^https?:\/\//.test(resolvedUrl)) {
    return resolvedUrl;
  }
  const url = new URL(resolvedUrl);
  if (API_BASE_PATHNAME && url.pathname.startsWith(`${API_BASE_PATHNAME}/`)) {
    return `${url.pathname.slice(API_BASE_PATHNAME.length)}${url.search}`;
  }
  if (API_BASE_PATHNAME && url.pathname === API_BASE_PATHNAME) {
    return url.search || "/";
  }
  return `${url.pathname}${url.search}`;
}

async function fetchWithAuth(request: Request, accessTokenOverride?: string | null) {
  const token = getStoredAuthSource() === "sso" ? null : (accessTokenOverride ?? getAccessToken());
  const headers = new Headers(request.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return fetch(
    new Request(request, {
      credentials: "include",
      headers,
    }),
  );
}

function handleUnauthorized(response: Response, detail: unknown) {
  const error = createApiErrorFromResponse(response, detail);
  clearSession();
  redirectToLogin();
  return error;
}

async function parseResponseBody<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  const contentLength = response.headers.get("content-length");
  if (contentLength === "0") {
    return undefined as T;
  }
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    const text = await response.text();
    return (text.length === 0 ? undefined : text) as T;
  }
  const text = await response.text();
  if (text.length === 0) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

async function refreshAccessToken() {
  if (getStoredAuthSource() === "sso") {
    const response = await fetch(`${API_BASE_URL}${LOCAL_AUTH_PATHS.bootstrap}`, {
      method: "POST",
      credentials: "include",
    });
    if (!response.ok) {
      return null;
    }
    const user = await parseResponseBody<UserSession>(response);
    setSession("exam-sso-cookie-session", user);
    setStoredAuthSource("sso");
    return "exam-sso-cookie-session";
  }
  const response = await fetch(`${API_BASE_URL}${LOCAL_AUTH_PATHS.refresh}`, {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) {
    return null;
  }
  const data = await parseResponseBody<{ access_token: string; user: UserSession }>(response);
  setSession(data.access_token, data.user);
  return data.access_token;
}

type RequestOptions = RequestInit & {
  bodyJson?: unknown;
};

export function getOpenApiClientBaseUrl() {
  return OPENAPI_CLIENT_BASE_URL;
}

export async function apiFetchRequest(request: Request) {
  return apiFetch(request);
}

export async function apiFetch(input: RequestInfo | URL, init: RequestInit = {}) {
  const request = buildRequest(input, init);
  let response = await fetchWithAuth(request.clone());
  if (response.ok) {
    return response;
  }
  if (
    response.status === 401 &&
    !LOCAL_AUTH_NON_REFRESHABLE_PATHS.has(extractApiRequestPath(input))
  ) {
    const refreshedAccessToken = await refreshAccessToken();
    if (refreshedAccessToken) {
      response = await fetchWithAuth(request.clone(), refreshedAccessToken);
      if (response.ok) {
        return response;
      }
    }
    const detail = await readErrorDetail(response);
    throw handleUnauthorized(response, detail);
  }
  return response;
}

async function sendRequest(
  path: string,
  options: RequestOptions = {},
  accessTokenOverride?: string | null,
): Promise<Response> {
  const { bodyJson, ...rest } = options;
  const requestInit: RequestInit = {
    ...rest,
    ...(bodyJson === undefined ? {} : { body: JSON.stringify(bodyJson) }),
  };
  if (accessTokenOverride) {
    return fetchWithAuth(buildRequest(path, requestInit), accessTokenOverride);
  }
  return apiFetch(path, requestInit);
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await sendRequest(path, options);
  if (!response.ok) {
    const detail = await readErrorDetail(response);
    if (response.status === 401 && !LOCAL_AUTH_NON_REFRESHABLE_PATHS.has(path)) {
      throw handleUnauthorized(response, detail);
    }
    throw createApiErrorFromResponse(response, detail);
  }
  return parseResponseBody<T>(response);
}

export async function downloadRequest(path: string, filename?: string): Promise<void> {
  const response = await sendRequest(path, { method: "GET" });
  if (!response.ok) {
    const detail = await readErrorDetail(response);
    if (response.status === 401) {
      throw handleUnauthorized(response, detail);
    }
    throw createApiErrorFromResponse(response, detail);
  }
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = filename ?? "download";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(blobUrl);
}

export { ApiError, createApiErrorFromResponse };
